import { Address } from '@adornis/address/db/address.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { validate } from '@adornis/validation/decorators.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { MAX_SERVICE_DESK_CC, parseEmailList } from '@nsc/smartteams-shared/api/service-desk-email.js';

@Entity()
export class SmartTeamsPartnerCompany extends MongoEntity {
  static override _class = 'SmartTeamsPartnerCompany';
  static override _collectionName = 'smartteams_partnercompanies';

  @validate(nonOptional())
  @Field(() => String)
  name: Maybe<string>;

  @Field(() => Address)
  address: Maybe<Address>;

  /**
   * The ServiceNow client (`client_list` sys_id) this company works for, e.g. the client "DELL" for the company
   * "NSC Dell". The per-company field engineer sync resolves the client's name from it and looks for that name in
   * each engineer's ServiceNow groups. Empty means the company cannot be synced.
   */
  @Field(() => String)
  serviceNowClientId: Maybe<string>;

  /**
   * Who the smartteams-app "Email service desk" button addresses for this company's engineers.
   * Editable by NSC Admin only (see ServiceDeskSettingsManage); read by the app via
   * @nsc/smartteams-shared/api/service-desk-email. Empty means the mail opens with no recipients filled in.
   */
  @validate(({ value, key, path }) => {
    if (!value) return;
    const { emails, invalid } = parseEmailList(value);
    if (invalid.length > 0 || emails.length !== 1) {
      throw new ValidationError('Enter exactly one valid email address', { key, path });
    }
  })
  @Field(() => String)
  serviceDeskTo: Maybe<string>;

  @validate(({ value, key, path }) => {
    if (!value) return;
    const { emails, invalid } = parseEmailList(value as string[]);
    if (invalid.length > 0) {
      throw new ValidationError(`Invalid email address: ${invalid.join(', ')}`, { key, path });
    }
    if (emails.length > MAX_SERVICE_DESK_CC) {
      throw new ValidationError(`At most ${MAX_SERVICE_DESK_CC} Cc addresses`, { key, path });
    }
  })
  @Field(() => [String])
  serviceDeskCc: Maybe<string[]>;

  @Field(() => Date)
  archivedAt: Maybe<Date>;

  @Field(() => Date)
  deletedAt: Maybe<Date>;

  @Field(() => Date, { default: v => v ?? new Date() })
  createdAt: Maybe<Date>;
}
